/**
 * Permit Wall top-zone card (DISPLAY-only office TV).
 *
 * One of the fixed five cards showing the most-recently-updated
 * transactions: transaction name (largest), number, the first lines of
 * the latest update, and a static status pill. When this card is the
 * newest arrival with an event, a repeating ~8 s flash overlay is laid on
 * top (driven entirely by the `flashEvent` prop + the CSS `wallFlash`
 * keyframes); the pill itself never flashes and never sounds.
 *
 * vw/vh units scale the whole layout to the TV size without media
 * queries, matching the prototype's approach.
 */

import type { WallEvent } from "../../../../../packages/keywords/index.js";
import { pillFor, WALL_EVENT_COLORS, type WallRow, type WallTheme } from "@/lib/wall";

interface Props {
  row: WallRow;
  theme: WallTheme;
  /** Non-null only for the single newest-arrival card. */
  flashEvent: WallEvent | null;
}

export function WallCard({ row, theme, flashEvent }: Props) {
  const pill = pillFor(theme, row.requestStatus);
  const updateColor = row.event ? WALL_EVENT_COLORS[theme][row.event].text : "var(--text)";
  const flashColor = flashEvent ? WALL_EVENT_COLORS[theme][flashEvent] : null;

  return (
    <div
      style={{
        flex: "1 1 0",
        minWidth: 0,
        position: "relative",
        background: "var(--card-bg)",
        border: "1px solid var(--card-border)",
        borderRadius: "1.2vw",
        padding: "2.3vh 1.6vw",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxShadow: "var(--card-shadow)",
      }}
    >
      {flashColor ? (
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "1.2vw",
            pointerEvents: "none",
            boxShadow: `inset 0 0 0 0.22vw ${flashColor.line}, inset 0 0 7vw ${flashColor.glow}`,
            background: `radial-gradient(120% 80% at 50% 0%, ${flashColor.glow}, transparent 72%)`,
            animation: "wallFlash 1.15s ease-in-out 7",
          }}
        />
      ) : null}

      <div
        style={{
          fontSize: "2.0vw",
          fontWeight: 800,
          lineHeight: 1.18,
          letterSpacing: "-0.012em",
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textWrap: "balance",
        }}
      >
        {row.name}
      </div>

      <div style={{ marginTop: "0.9vh", fontSize: "1.3vw", fontWeight: 700, color: "var(--num)", textAlign: "right" }}>
        <span dir="ltr" style={{ unicodeBidi: "isolate", fontVariantNumeric: "tabular-nums", letterSpacing: "0.04em" }}>
          {row.number}
        </span>
      </div>

      <div style={{ flex: 1 }} />

      <div
        style={{
          fontSize: "1.4vw",
          fontWeight: 700,
          lineHeight: 1.32,
          color: updateColor,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          marginBottom: "1.6vh",
        }}
      >
        {row.update}
      </div>

      {row.requestStatus ? (
        <div style={{ display: "flex" }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.55vw",
              padding: "0.8vh 1.05vw",
              borderRadius: "0.8vw",
              fontSize: "1.2vw",
              fontWeight: 800,
              background: pill.bg,
              color: pill.fg,
            }}
          >
            <span style={{ width: "0.85vw", height: "0.85vw", borderRadius: "50%", background: pill.dot, flex: "none" }} />
            {row.requestStatus}
          </div>
        </div>
      ) : null}
    </div>
  );
}
